import React from 'react';
import LoadingScreen from 'components/Loading/Screen';
import useInviter, { LOAD_INVITER_ERROR_TYPE } from 'data/inviter/useInviter';
import { useMasthead } from 'data/masthead/useMasthead';
import { usePlan } from 'data/plan/usePlan';
import { useTranslation } from 'data/translation/useTranslation';
import { useUserData } from 'data/user/useUserData';
import { Locale } from 'lib/i18n';
import { trackEvent, withTrackLinkEvent } from 'lib/tracking';
import {
  joinLandingPageAuth,
  joinLandingPageNonAuth,
} from 'lib/trackingEvents';
import { buildUrlStrict } from 'lib/url';
import { useRouter } from 'next/router';
import { ReactNode, useEffect } from 'react';

import GeneralError from 'containers/Error/GeneralError';
import InvitationExpiredError from 'containers/Error/InvitationExpiredError';
import PlanAlreadyFullError from 'containers/Error/PlanAlreadyFullError';
import { useError } from 'data/error/useError';
import { isErrorOfTypeAndCode } from 'data/error/utils';

type TrackPageViewProps = {
  onPageView: () => void;
  children: ReactNode;
};

const TrackPageView = ({ onPageView, children }: TrackPageViewProps) => {
  useEffect(() => {
    onPageView();
  }, [onPageView]);
  return <>{children}</>;
};

export function withInviterLoading(SubLandingPageComponent: any) {
  return () => {
    const { themes, planType } = usePlan();
    const router = useRouter();
    const mastheadProps = useMasthead(router.locale as Locale);
    const { error, errors } = useError();
    const { userData } = useUserData();
    const { locale } = useTranslation();

    const { inviteToken, isLoadingInviter } = useInviter();

    const inviterError = errors.find(
      e => e?.type === LOAD_INVITER_ERROR_TYPE && e?.status !== 401,
    );

    const shouldDisplayGeneralError = typeof inviterError !== 'undefined';

    if (error) {
      if (
        isErrorOfTypeAndCode(error, LOAD_INVITER_ERROR_TYPE, 'PLAN_IS_FULL')
      ) {
        return <PlanAlreadyFullError error={error} />;
      }

      if (
        isErrorOfTypeAndCode(error, LOAD_INVITER_ERROR_TYPE, 'INVITE_NOT_FOUND')
      )
        return <InvitationExpiredError error={error} />;
    }

    if (shouldDisplayGeneralError) {
      return <GeneralError error={inviterError} />;
    }

    if (isLoadingInviter || mastheadProps.isLoading) {
      return <LoadingScreen theme={themes.light} />;
    }

    const isAnonymous = !userData;
    const senderLogOptions = isAnonymous
      ? { authorize: false, flush: true }
      : {};
    const event = isAnonymous ? joinLandingPageNonAuth : joinLandingPageAuth;

    return (
      <TrackPageView
        onPageView={() => trackEvent(event.getPageView(), senderLogOptions)}
      >
        <SubLandingPageComponent
          theme={themes.dark}
          joinUrl={buildUrlStrict(planType, 'SUB_CONFIRM_ACCOUNT', locale, {
            token: inviteToken!,
          })}
          termsUrl={buildUrlStrict(planType, 'LEGAL_TERMS', locale)}
          onJoinClick={withTrackLinkEvent(event.getJoin(), senderLogOptions)}
          onTermsClick={withTrackLinkEvent(event.getToc(), senderLogOptions)}
          mastheadData={mastheadProps.data}
        />
      </TrackPageView>
    );
  };
}
